import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './ClippedText.module.css';

interface Props {
  /** The full text. It is always in the DOM, so assistive technology reads all of it. */
  children: string;
  className?: string;
  /**
   * Inside a control that is itself the focus / tap target (a list row that is a button): only
   * the native title is added; the control's own detail view shows the full text.
   */
  passive?: boolean;
}

/**
 * One line of text that never wraps: what does not fit is cut with an ellipsis. Only when it IS cut
 * does the text become reachable by every input: hover shows the native title, keyboard focus and a
 * tap show the full text in a small bubble (Escape / blur / tapping elsewhere closes it).
 */
export function ClippedText({ children, className, passive = false }: Props) {
  const id = useId();
  const node = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);
  const [open, setOpen] = useState(false);
  const [spot, setSpot] = useState<CSSProperties>({});

  useEffect(() => {
    const element = node.current;
    if (!element) return;
    const measure = () => {
      setClipped(element.scrollWidth > element.clientWidth + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [children]);

  useEffect(() => {
    if (!open) return;
    const close = () => {
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const show = () => {
    const element = node.current;
    if (!element || !clipped) return;
    const box = element.getBoundingClientRect();
    const width = Math.min(20 * 16, window.innerWidth - 16);
    setSpot({
      top: box.bottom + 4,
      left: Math.max(8, Math.min(box.left, window.innerWidth - width - 8)),
      maxWidth: width,
    });
    setOpen(true);
  };

  return (
    <>
      <span
        ref={node}
        className={`${styles.clip} ${className ?? ''}`}
        {...(clipped && { title: children })}
        {...(clipped &&
          !passive && {
            tabIndex: 0,
            'aria-describedby': open ? id : undefined,
            onFocus: show,
            onBlur: () => {
              setOpen(false);
            },
            onClick: show,
          })}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <span id={id} role="tooltip" className={styles.bubble} style={spot}>
            {children}
          </span>,
          document.body,
        )}
    </>
  );
}
